import { useData } from "@robe/client/xcore-ui";
import { AspectRatio, Card, CardProps, Flex, Img, Typography } from "@xcorejs/ui";
import Richtext from "design-system/Richtext";
import { resolveSrcSet } from "utils";
import { Video } from "xcore/types";

type VideoCardProps = {
  value: Pick<Video<"title" | "videoThumbnail" | "description">, "values">;
} & CardProps;

const VideoCard = ({ value, ...props }: VideoCardProps) => {
  const { stringify, image, localize } = useData();

  const name = stringify(value.values.title);
  const description = localize(value.values.description);

  const imgSrc = image(value.values.videoThumbnail, { width: 410, height: 230 });
  const bigImgSrc = image(value.values.videoThumbnail, { width: 410, height: 230, enlargement: 2 });

  if (!imgSrc) {
    return null;
  }

  return (
    <Card
      variant="outline"
      media={
        <AspectRatio ratio={23 / 41} width="100%">
          <Img src={imgSrc} {...resolveSrcSet(imgSrc, bigImgSrc)} alt={name} width="100%" height="100%" loading="lazy" />
        </AspectRatio>
      }
      _media={{ justifyContent: "center" }}
      body={
        (description || name) && (
          <Flex flexDirection="column" position="relative" zIndex={1000} alignItems="center" width="100%">
            <Typography variant="h4" fontSize="1.5rem" lineHeight="2rem" textAlign="center">
              {name}
            </Typography>
            {description && (
              <Typography as="div" mt="1rem" variant="p" fontSize="1.5rem" lineHeight="2rem" textAlign="center" wordBreak="break-word">
                <Richtext value={description} />
              </Typography>
            )}
          </Flex>
        )
      }
      _body={{ padding: "2rem 3rem 3rem 3rem" }}
      transition="box-shadow 300ms, border 300ms"
      boxShadow="0 3px 10px 0 rgba(30, 30, 30, 0.25)"
      _hover={{ boxShadow: "0 0.6rem 1.5rem 0 rgba(30, 30, 30, 0.5)" }}
      border={0}
      maxWidth="100%"
      {...props}
    />
  );
};

export default VideoCard;
